import { type Component, createSignal, onMount, Show } from 'solid-js';
import { gameStore } from '@/stores/gameStore';
import { validateTeamName, validatePassword } from '@/utils/validation';
import { useTranslation } from '@/i18n';
import LoadingSpinner from './LoadingSpinner';

const LoginForm: Component = () => {
  const { t } = useTranslation();
  const [teamName, setTeamName] = createSignal('');
  const [password, setPassword] = createSignal('');
  const [showPassword, setShowPassword] = createSignal(false);
  const [formErrors, setFormErrors] = createSignal<{ teamName?: string; password?: string }>({});

  // Check synchronously if selfservice auto-login is needed (before first render)
  const hasSelfserviceCredentials = !!(
    localStorage.getItem('selfservice_team_name') &&
    localStorage.getItem('selfservice_team_password')
  );
  const [autoLoggingIn, setAutoLoggingIn] = createSignal(hasSelfserviceCredentials);

  // Perform the auto-login in onMount
  onMount(async () => {
    const savedTeamName = localStorage.getItem('selfservice_team_name');
    const savedPassword = localStorage.getItem('selfservice_team_password');

    if (savedTeamName && savedPassword) {
      // Clear stored credentials
      localStorage.removeItem('selfservice_team_name');
      localStorage.removeItem('selfservice_team_password');

      // Auto-login
      setTeamName(savedTeamName);
      setPassword(savedPassword);

      try {
        await gameStore.login(savedTeamName, savedPassword);
      } catch (error) {
        console.error('Auto-login failed:', error);
        setAutoLoggingIn(false);
      }
    }
  });

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    
    // Clear previous errors
    setFormErrors({});
    gameStore.clearError('login');

    // Validate form
    const teamNameValidation = validateTeamName(teamName());
    const passwordValidation = validatePassword(password(), false);

    const errors: { teamName?: string; password?: string } = {};
    if (!teamNameValidation.isValid) errors.teamName = teamNameValidation.error;
    if (!passwordValidation.isValid) errors.password = passwordValidation.error;

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      await gameStore.login(teamName().trim(), password().trim());
    } catch (error) {
      // Error is already handled in the store
      console.error('Login error:', error);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword());
  };

  return (
    <div class="w-full max-w-md mx-auto">
      {/* Loading screen during selfservice auto-login */}
      <Show when={autoLoggingIn()}>
        <div class="card">
          <div class="card-body py-16">
            <div class="flex flex-col items-center justify-center gap-4">
              <LoadingSpinner size="lg" />
              <p class="text-primary-200 text-lg font-medium animate-pulse">
                Wchodzę do gry...
              </p>
            </div>
          </div>
        </div>
      </Show>

      {/* Normal login form */}
      <Show when={!autoLoggingIn()}>
      <div class="card">
        <div class="card-header text-center">
          <h1 class="text-3xl font-bold mb-3 text-white drop-shadow-glow">
            {t('login.title')}
          </h1>
          <p class="text-primary-200 text-lg">
            {t('login.subtitle')}
          </p>
        </div>

        <div class="card-body">
          <form onSubmit={handleSubmit} noValidate>
            {/* Team Name Input */}
            <div class="form-group">
              <label for="teamName" class="form-label">
                {t('login.teamName')}
              </label>
              <input
                type="text"
                id="teamName"
                class={`form-input ${formErrors().teamName || gameStore.errorState.login ? 'error' : ''}`}
                placeholder={t('login.teamNamePlaceholder')}
                value={teamName()}
                onInput={(e) => {
                  setTeamName(e.currentTarget.value);
                  if (formErrors().teamName) {
                    setFormErrors(prev => ({ ...prev, teamName: undefined }));
                  }
                }}
                disabled={gameStore.loadingState.login}
                autocomplete="username"
                autocapitalize="words"
                spellcheck={false}
              />
              {formErrors().teamName && (
                <div class="form-error">
                  {formErrors().teamName}
                </div>
              )}
            </div>

            {/* Password Input (Optional) */}
            <div class="form-group">
              <label for="password" class="form-label">
                {t('login.password')}
              </label>
              <div class="relative">
                <input
                  type={showPassword() ? 'text' : 'password'}
                  id="password"
                  class={`form-input ${formErrors().password ? 'error' : ''}`}
                  placeholder={t('login.passwordPlaceholder')}
                  value={password()}
                  onInput={(e) => {
                    setPassword(e.currentTarget.value);
                    if (formErrors().password) {
                      setFormErrors(prev => ({ ...prev, password: undefined }));
                    }
                  }}
                  disabled={gameStore.loadingState.login}
                  autocomplete="current-password"
                />
                {password() && (
                  <button
                    type="button"
                    class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    onClick={togglePasswordVisibility}
                    disabled={gameStore.loadingState.login}
                  >
                    {showPassword() ? '👁️' : '👁️‍🗨️'}
                  </button>
                )}
              </div>
              {formErrors().password && (
                <div class="form-error">
                  {formErrors().password}
                </div>
              )}
            </div>

            {/* Login Error */}
            {gameStore.errorState.login && (
              <div class="mb-4 p-4 bg-accent-500/20 border border-accent-500/50 rounded-game">
                <div class="flex items-start">
                  <span class="text-accent-400 mr-2">⚠️</span>
                  <div class="text-accent-300 text-sm">
                    {t('errors.loginFailed')}
                  </div>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              class="btn btn-primary btn-full btn-lg"
              disabled={gameStore.loadingState.login || !teamName().trim()}
            >
              {gameStore.loadingState.login ? (
                <>
                  <LoadingSpinner size="sm" />
                  {t('login.joiningGame')}
                </>
              ) : (
                <>
                  🚀 {t('login.joinGame')}
                </>
              )}
            </button>
          </form>
        </div>

        <div class="card-footer">
          <div class="text-center text-primary-200">
            <p class="text-lg font-semibold">{t('login.readyToExplore')}</p>
            <div class="mt-4 pt-4 border-t border-primary-600/50 space-y-2">
              <a href="/selfservice" class="block text-blue-300 hover:text-white transition-colors text-sm">
                📦 Masz kod z pudełka? Kliknij tutaj
              </a>
              <a href="/admin" class="block text-primary-300 hover:text-white transition-colors text-sm">
                {t('login.adminAccess')}
              </a>
            </div>
          </div>
        </div>
      </div>
      </Show>
    </div>
  );
};

export default LoginForm;